/**
 * Helpers for rendering a stored photo/avatar value that may arrive as:
 *   - a data URI ("data:image/...")
 *   - an HTTP(S) URL ("https://...")
 *   - a same-origin path ("/api/user-images/<token>?v=...")
 *   - raw base64 (legacy columns)
 *   - a short text fallback (emoji / initial / null)
 *
 * Since the backend migrated from base64-in-JSON to URL strings, many
 * components must accept URL paths as valid <img src> values without
 * accidentally wrapping them in `data:image/jpeg;base64,...`.
 */

export function isImageSrc(value) {
  return typeof value === 'string' && (
    value.startsWith('data:') ||
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('/')
  );
}

export function toImageSrc(value) {
  if (!value || typeof value !== 'string') return '';
  if (isImageSrc(value)) return value;
  return `data:image/jpeg;base64,${value}`;
}

// Commons media reaches us from MORE THAN ONE HOST: upload.wikimedia.org and
// thumb.wikimedia.org both serve the same renders, and 88,809 of the 290,885
// landmark photo rows on dev carry the thumb host. Matching the host name
// `upload.wikimedia.org` silently excluded every one of them — they skipped
// resizing (a 1920px source into a 224px card) and got no Commons credit link,
// which for a CC BY-SA corpus is a licence problem and not only a weight one.
// Match the SHAPE instead: a wikimedia.org media host serving a /wikipedia/
// path. The rewrites below still key off the path, which is identical on both.
const WIKIMEDIA_MEDIA_RE = /^https?:\/\/[a-z0-9-]+\.wikimedia\.org\/wikipedia\//i;

export function isWikimediaMediaUrl(url) {
  return typeof url === 'string' && WIKIMEDIA_MEDIA_RE.test(url);
}

// Rewrite a Wikimedia Commons thumb URL to a different render width so we
// don't ship 1920px sources into 64px list thumbs and 280px hero cards.
// Wikimedia thumb URLs end in `/<width>px-<filename>` and the image server
// generates the requested size on demand — same auth, same caching.
//
// Pass-through for anything that isn't a Wikimedia URL, so callers can blindly
// run every <img src> through this.
//
// Our own /api/user-photos/<id> URLs are sized via the `size` query param
// (handled separately by sizedPhotoUrl below).
const WIKIMEDIA_THUMB_RE = /(\/thumb\/[^/]+\/[^/]+\/[^/]+\/)(\d+)px-([^/?#]+)$/;

// A bare Commons ORIGINAL: /wikipedia/commons/<a>/<ab>/<File>. These carry no
// width segment, so the thumb regex cannot touch them and they used to ship at
// full resolution — 18,945 of 71,885 rows in wikimedia_photos store one in
// thumb_url (the P18 sweep hotlinks whatever Wikidata names), and they run to
// several megabytes; one measured 3.4 MB into a 64px stop thumbnail. Commons
// serves a scaled render of the same file under
// /thumb/<a>/<ab>/<File>/<width>px-<File>, which is what this builds.
//
// Scoped to /commons/ rather than any project: all 71,885 corpus rows are
// Commons, and a per-wiki upload's thumbnail scheme is not worth assuming for
// zero rows of benefit.
const WIKIMEDIA_ORIGINAL_RE = /^(https?:\/\/[a-z0-9-]+\.wikimedia\.org\/wikipedia\/commons\/)([0-9a-f])\/([0-9a-f]{2})\/([^/?#]+)$/i;

// Only raster formats thumbnail to `<width>px-<file>` unchanged. SVG, PDF, TIFF
// and video render to a DIFFERENT extension (`…px-File.svg.png`), so rewriting
// them by pattern produces a 404. Left alone: rare, and a working large image
// beats a broken small one.
const WIKIMEDIA_THUMBABLE_RE = /\.(jpe?g|png|gif|webp)$/i;

// Wikimedia renders thumbnails at a FIXED set of widths; anything else answers
// 400 (measured: 500 and 1280 return an image, 437 returns 400). Callers that
// compute a width from a layout therefore have to snap to the allowlist, and
// the one place that can enforce that is here. Snap UP so a card is never fed
// fewer pixels than it renders; past the largest step, ask for the largest.
const WIKIMEDIA_THUMB_WIDTHS = [20, 40, 60, 120, 250, 330, 500, 960, 1280, 1920, 3840];

function snapWikimediaWidth(width) {
  return WIKIMEDIA_THUMB_WIDTHS.find(step => step >= width) ?? WIKIMEDIA_THUMB_WIDTHS[WIKIMEDIA_THUMB_WIDTHS.length - 1];
}

export function wikimediaResize(url, width) {
  if (!url || typeof url !== 'string' || !Number.isFinite(width) || width <= 0) {
    return url;
  }
  if (!isWikimediaMediaUrl(url)) return url;
  const snapped = snapWikimediaWidth(width);
  if (WIKIMEDIA_THUMB_RE.test(url)) {
    return url.replace(WIKIMEDIA_THUMB_RE, (_, prefix, _oldWidth, file) => `${prefix}${snapped}px-${file}`);
  }
  return url.replace(WIKIMEDIA_ORIGINAL_RE, (whole, prefix, d1, d2, file) => (
    WIKIMEDIA_THUMBABLE_RE.test(file)
      ? `${prefix}thumb/${d1}/${d2}/${file}/${snapped}px-${file}`
      : whole
  ));
}

// Rewrite a Tiqets imgix CDN URL to a different render size. Tiqets cover
// URLs are imgix links carrying explicit &w=<n>&h=<n> params (square crops,
// e.g. ...&fit=crop&h=250&q=70&w=250) — imgix resizes on demand, so we're not
// limited to the 4 sizes the backend stored. Rewrite both w and h to the
// target edge so a 250px-stored cover can render crisp at 500/1280px.
//
// Pass-through for anything that isn't a Tiqets imgix URL.
const TIQETS_HOST = 'aws-tiqets-cdn.imgix.net';
const IMGIX_W_RE = /([?&])w=\d+/;
const IMGIX_H_RE = /([?&])h=\d+/;

export function tiqetsResize(url, edge) {
  if (!url || typeof url !== 'string' || !Number.isFinite(edge) || edge <= 0) {
    return url;
  }
  if (!url.includes(TIQETS_HOST)) return url;
  let out = url;
  out = IMGIX_W_RE.test(out) ? out.replace(IMGIX_W_RE, `$1w=${edge}`) : `${out}&w=${edge}`;
  out = IMGIX_H_RE.test(out) ? out.replace(IMGIX_H_RE, `$1h=${edge}`) : `${out}&h=${edge}`;
  return out;
}

// Rewrite a /api/user-photos/<token>... URL to request a different size variant.
// Only the query string is inspected, never the token segment.
// Idempotent: replaces an existing &size= or appends one. Pass-through for
// anything that isn't a same-origin user-photos URL.
const SIZE_PARAM_RE = /([?&])size=[^&]*/;

// R2/CDN user-photo URLs carry the size as a PATH segment instead:
// https://<cdn>/photos/<16-char-token>/<size>?v=… (object stores select by
// key, so a query param can't pick a variant). Host-agnostic match — same
// approach as the Wikimedia/Tiqets rewriters. The exact 16-char token match
// mirrors PHOTO_TOKEN_PATTERN in server/utils/photoToken.js.
const CDN_PHOTO_SIZE_RE = /(\/photos\/[A-Za-z0-9_-]{16}\/)(thumb|medium|large|xlarge|full)(?=$|\?)/;

export function isCdnPhotoUrl(url) {
  return typeof url === 'string' && CDN_PHOTO_SIZE_RE.test(url);
}

export function sizedPhotoUrl(url, size) {
  if (!url || typeof url !== 'string' || !size) return url;
  if (CDN_PHOTO_SIZE_RE.test(url)) {
    return url.replace(CDN_PHOTO_SIZE_RE, `$1${size}`);
  }
  if (!url.includes('/api/user-photos/')) return url;
  if (SIZE_PARAM_RE.test(url)) {
    return url.replace(SIZE_PARAM_RE, `$1size=${size}`);
  }
  return url + (url.includes('?') ? `&size=${size}` : `?size=${size}`);
}

// One-shot helper: pick the right rendered width for the surface, then apply
// the appropriate rewrite to whatever upstream returned. `size` is one of
// 'thumb' (64px list), 'medium' (~280px card), 'large' (default, hero), 'xlarge'.
//
// IMPORTANT: Wikimedia restricts thumbnail widths to a fixed allowlist —
// arbitrary widths return 400. The allowlist (per https://w.wiki/GHai) is
// 20, 40, 60, 120, 250, 330, 500, 960, 1280, 1920, 3840, and wikimediaResize
// snaps to it. Keep these values ON the list anyway, so the requested width is
// the rendered width.
const SIZE_TO_WIKIMEDIA_WIDTH = {
  thumb: 120,    // 64 CSS px = 128 retina; snap up to 120 (slight downscale, imperceptible)
  medium: 500,   // ~280 CSS px = ~560 retina; snap to 500 (slight upscale, imperceptible) — 960 is next allowed step
  large: 1280,
  xlarge: 1920
};

// Tiqets imgix renders any edge on demand, so we just map each surface to a
// square edge (Tiqets covers are square crops). Matches the backend's stored
// step sizes (128/250/500/1284) so cached imgix renders are reused where they
// overlap; hero/map surfaces request a crisp edge instead of the 250px default.
const SIZE_TO_TIQETS_EDGE = {
  thumb: 128,
  medium: 500,   // card/info hero on retina — 250px stored was the blur culprit
  large: 1284,
  xlarge: 1284   // imgix caps at the source; 1284 is the largest Tiqets serves
};

export function resizeForSurface(url, size) {
  if (!url || typeof url !== 'string' || !size) return url;
  // includes() not startsWith() — URLs may be absolute (`http://host/api/user-photos/...`)
  // when API_BASE has been prepended, see PlannerTripCard, useMapCollections, etc.
  if (url.includes('/api/user-photos/') || isCdnPhotoUrl(url)) return sizedPhotoUrl(url, size);
  if (isWikimediaMediaUrl(url)) {
    const width = SIZE_TO_WIKIMEDIA_WIDTH[size];
    return width ? wikimediaResize(url, width) : url;
  }
  if (url.includes('aws-tiqets-cdn.imgix.net')) {
    const edge = SIZE_TO_TIQETS_EDGE[size];
    return edge ? tiqetsResize(url, edge) : url;
  }
  return url;
}

// The Commons file page for a Wikimedia media URL, or null.
//
// This is what lets the Wikimedia credit be licence-compliant without the
// backend plumbing author and licence through all eleven cover cascades.
// CC BY-SA 4.0 §3(a)(2) allows the attribution conditions to be satisfied "by
// providing a URI or hyperlink to a resource that includes the required
// information", and the file page carries author, licence and source in the
// form Commons itself publishes. "Photo: Wikimedia Commons" as bare text names
// neither author nor licence and does NOT satisfy CC BY-SA; the same words
// linked here do. That matters concretely — most of the landmark photo corpus
// is CC BY-SA, where credit is a licence condition rather than a courtesy.
//
// Both URL shapes occur in our data, on either media host:
//   thumb     /wikipedia/commons/thumb/a/ab/Foo.jpg/500px-Foo.jpg
//   original  /wikipedia/commons/a/ab/Foo.jpg
// Only /commons/ is handled: /wikipedia/en/ and friends are per-wiki local
// uploads with no Commons file page, and inventing one would 404. The filename
// stays percent-encoded, which is the form wiki URLs take.
const COMMONS_THUMB_RE = /\/wikipedia\/commons\/thumb\/[0-9a-f]\/[0-9a-f]{2}\/([^/?#]+)\//;
const COMMONS_FILE_RE = /\/wikipedia\/commons\/[0-9a-f]\/[0-9a-f]{2}\/([^/?#]+?)(?:[?#]|$)/;

export function wikimediaFilePageUrl(url) {
  if (!isWikimediaMediaUrl(url)) return null;
  const m = url.match(COMMONS_THUMB_RE) || url.match(COMMONS_FILE_RE);
  return m ? `https://commons.wikimedia.org/wiki/File:${m[1]}` : null;
}

// Derive the required photo credit from a resolved cover-photo URL, keyed off
// the CDN host. Used where the cascade hands the frontend only a URL (no
// attribution field) but the source's terms still require a credit — e.g. the
// social feed's `place` cards, whose backend query emits no tiqets/wikimedia
// block, and tourism-experience covers resolved from a tour's POI links.
// Returns null when the host needs no credit (user uploads, category
// fallbacks). Google covers need an async lookup (author, avatar, Maps link),
// so they're credited by PhotoCredit from the same URL, not here.
//
// Returns the `{ text, uri }` object form — already understood by ActivityCard,
// CompactActivityCard and PhotoCarousel, which render it as a hyperlink —
// whenever a Commons file page can be derived, and the bare string when it
// cannot. The per-image Tiqets `credits` and the Wikimedia author/licence text
// aren't in the URL, so this stays the host-level minimum: when a richer
// attribution field IS present, prefer it over this fallback.
export function attributionForImageUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.includes(TIQETS_HOST)) return 'Photo: Tiqets';
  if (isWikimediaMediaUrl(url)) {
    const filePage = wikimediaFilePageUrl(url);
    return filePage
      ? { text: 'Photo: Wikimedia Commons', uri: filePage }
      : 'Photo: Wikimedia Commons';
  }
  return null;
}
